import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import { fetchPigGroup } from 'reducers/pigGroups/pigGroupShow';
import { setPageOptions } from 'reducers/layout';
// components
import { FormattedMessage } from 'react-intl';
import Button from 'components/Button';
import Link from 'react-router/lib/Link';
import CenterBox from 'components/CenterBox';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import ShipmentForm from './components/ShipmentForm';
import PigsUnderCareLine from 'components/PigsUnderCareLine';
import TruckingRouteCard from 'components/TruckingRouteCard/TruckingRouteCard';
import Preloader from 'components/Preloader';
import MessageBox from 'components/MessageBox';
// hooks
import useFetchById from 'hooks/useFetchById';
// utils
import moment from 'moment';
import pick from 'lodash.pick';
import { showToastrMessage } from 'utils';
import { formatBackEndWeightToSystem, formatWeightToBackEndFormat } from 'utils/weightHelper';
import { getCommitmentDirection, getAvgWeightForShipment } from 'utils/commitmentsHelper';
import { getCommitmentPlacementDate } from 'utils/pigGroupsHelper';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// api
import { createPigGroupMigration, getPigGroupCommitments } from 'endpoints/pigGroups';
import { getCommitment } from 'endpoints/commitments';
import PigsUnderTreatmentLine from '../../../components/PigsUnderTreatmentLine';

const ShipmentCreate = ({
  fetchPigGroup,
  location: { query, pathname },
  measurementSystem,
  params: { id: paramsGroupId },
  pigGroup,
  router,
  setPageOptions,
  submit,
  isLogisticsEnabled,
}) => {
  const queryCommitmentId = query?.commitmentId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingCommitments, setIsFetchingCommitments] = useState(false);
  const [commitmentsCount, setCommitmentsCount] = useState(0);

  useEffect(() => {
    if (isLogisticsEnabled) {
      setIsFetchingCommitments(true);
      getPigGroupCommitments(paramsGroupId, { commitment_type: 'destination' }).then(({ meta }) => {
        setIsFetchingCommitments(false);
        setCommitmentsCount(meta.total);
      });
    }
  }, []);

  const [{ data: commitment, isLoading }] = useFetchById(getCommitment, queryCommitmentId, {
    onSuccess: (commitment) => {
      if (commitment.pig_group_id !== +paramsGroupId || commitment.status !== 'accepted') {
        router.push(pathname);
      }
    },
    onError: () => router.push(pathname),
  });

  useEffect(() => {
    if (pigGroup.id !== parseInt(paramsGroupId, 10)) {
      fetchPigGroup(paramsGroupId);
    }
    setPageOptions({ mobileTitle: 'createShipment' });
    return () => {
      setPageOptions({ mobileTitle: '' });
    };
  }, []);

  const prepareResource = (data) => {
    const resource = pick(data, ['amount', 'condition', 'treated_amount']);

    return {
      ...resource,
      delivery_date: data.delivery_date.format('YYYY-MM-DD'),
      loading_commitment_id: commitment?.id,
      barn_ids: data.barns.length ? data.barns.map((barn) => barn.id) : [],
      migration_type: 'placement',
      estimated_weight: formatWeightToBackEndFormat(data.estimated_weight, measurementSystem),
    };
  };

  const handleSubmitData = (data) => {
    const resource = prepareResource(data);

    setIsSubmitting(true);
    createPigGroupMigration(paramsGroupId, { resource })
      .then(() => {
        showToastrMessage('component.toastr.shipment.successCreated');
        router.goBack();
      })
      .catch((response) => {
        setIsSubmitting(false);
        toastResponseErrors(response);
      });
  };

  const getInitialValues = () => {
    const initialBarns = [{}];
    if (!queryCommitmentId || !commitment?.id) return {
      delivery_date: moment(),
      barns: initialBarns,
    };
    const calculatedAvgWeight = getAvgWeightForShipment(commitment.loading.commitments);
    return {
      delivery_date: getCommitmentPlacementDate(commitment.arrive_at, pigGroup.created_on),
      amount: commitment.head_count,
      estimated_weight: formatBackEndWeightToSystem(calculatedAvgWeight, measurementSystem),
      barns: commitment.barn_id ? [{ id: commitment.barn_id }] : initialBarns,
    };
  };

  const cancelLink = commitment?.id
    ? `/groups/${paramsGroupId}/scheduled-loads`
    : `/groups/${paramsGroupId}/shipments`;

  return (
    <CenterBox>
      <Preloader isActive={isLoading || isSubmitting || isFetchingCommitments} />
      <section className="center-box-header show-for-large">
        <div className="title">
          <FormattedMessage id="general.createShipmentWithName" values={{ name: pigGroup.name }} />
        </div>
        <Link to={cancelLink}><i className="fa fa-times close-center-box" /></Link>
      </section>

      <PigsUnderCareLine pigsCount={pigGroup.pigs} mobile />
      {pigGroup.abf_tracking && <PigsUnderTreatmentLine pigsCount={pigGroup.treated_pigs} mobile />}

      {!isFetchingCommitments && !!commitmentsCount && !commitment && !isLoading && (
        <div className="pt-15 ph-15">
          <MessageBox type="warning">
            <FormattedMessage
              id="component.checkupSegment.pendingLoadsCount"
              values={{
                count: commitmentsCount,
                a: (msg) => (
                  <Link className="primary" to={`/groups/${paramsGroupId}/scheduled-loads`}>
                    {msg}
                  </Link>
                ),
              }}
            />
          </MessageBox>
        </div>
      )}

      <section className="center-box-body">
        {commitment && (
          <TruckingRouteCard
            className="mh-20 mb-20"
            direction={getCommitmentDirection(commitment)}
            commitments={commitment.loading.commitments}
            loadType={commitment.loading.load_type}
          />
        )}
        <ShipmentForm
          pigGroup={pigGroup}
          groupFarmId={pigGroup.farm_id}
          farmType={pigGroup.farm?.farm_type}
          initialValues={getInitialValues()}
          onSubmit={handleSubmitData}
          minDeliveryDate={moment(pigGroup.created_on)}
        />
      </section>

      <section className="center-box-footer show-for-large">
        <div className="buttons text-right">
          <Link className="button margin-right-1" to={cancelLink}>
            <FormattedMessage id="general.button.cancel" />
          </Link>
          <Button
            primary
            className="submit wider"
            onClick={() => submit('group-shipment')}
            disabled={isLoading || isSubmitting || isFetchingCommitments}
          >
            <FormattedMessage id="general.button.createShipment" />
          </Button>
        </div>
      </section>

      <StickyFooter className="hide-for-large">
        <Button
          primary
          className="submit"
          onClick={() => submit('group-shipment')}
          disabled={isLoading || isSubmitting || isFetchingCommitments}
        >
          <FormattedMessage id="general.button.createShipment" />
        </Button>
      </StickyFooter>
    </CenterBox>
  );
};

ShipmentCreate.propTypes = {
  measurementSystem: T.string.isRequired,
  params: T.object.isRequired,
  router: T.object.isRequired,
  location: T.object.isRequired,
  pigGroup: T.object,
  submit: T.func.isRequired,
  fetchPigGroup: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  isLogisticsEnabled: T.bool.isRequired,
};

export default connect(
  (state) => ({
    pigGroup: state.pigGroups.groupShow.data,
    measurementSystem: state.auth.user.current_company.measurement_system,
    isLogisticsEnabled: state.auth.user.current_company.logistics,
  }), {
    submit,
    fetchPigGroup,
    setPageOptions,
  }
)(ShipmentCreate);
